/**
 * Hook for submitting ALI discrepancy reports
 */
import { useState } from 'react';
import { useStore } from 'react-redux';
import { createFetchRetry } from '@rsos/capstone';
import { useCurrentPSAP } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import { useSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { useCurrentUser } from '@rsos/sinatra/src/selectors';
import { getAPIHost } from '@rsos/utils/metaTags';
import useCallDetails from '../JurisdictionView/EventPane/useCallDetails';
import { AliDiscrepancyFormData } from './AliDiscrepancyForm/AliDiscrepancyForm';

interface UseFormSubmitReturn {
  submitDiscrepancy: (formData: AliDiscrepancyFormData) => Promise<boolean>;
  error: string | null;
  success: boolean;
}

export interface DiscrepancySubmitResponse {
  success: boolean;
  discrepancy_id?: string;
  message?: string;
  error?: string;
}

export interface DiscrepancySubmitPayload {
  caller_id: string;
  account_id: string;
  discrepancy_type: 'ALI Error';
  call_type?: string;
  original_telephone_number: string;
  original_name?: string;
  original_address: string;
  original_community?: string;
  original_esn?: string;
  corrected_telephone_number?: string;
  corrected_name?: string;
  corrected_address?: string;
  corrected_community?: string;
  corrected_esn?: string;
  comments?: string;
  call_timestamp?: string;
}

export const useFormSubmit = (): UseFormSubmitReturn => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Get current PSAP and call data using proper selectors
  const currentPSAP = useCurrentPSAP();
  const currentUser = useCurrentUser();
  const selectedCallerID = useSelectedCallerID();
  const callDetails = useCallDetails(selectedCallerID || '');
  const callTimestamp = callDetails?.lastTimestamp
    ? new Date(callDetails.lastTimestamp)
    : null;
  const store = useStore();

  const orgName = currentUser?.currentOrg?.name;
  if (!orgName) {
    throw new Error("Current user's organization name not available");
  }

  const submitDiscrepancy = async (
    formData: AliDiscrepancyFormData,
  ): Promise<boolean> => {
    setError(null);
    setSuccess(false);

    try {
      if (!currentPSAP?.account_id) {
        throw new Error('PSAP account ID not available');
      }

      if (!selectedCallerID) {
        throw new Error('No call selected');
      }

      // Build payload
      const payload: DiscrepancySubmitPayload = {
        caller_id: selectedCallerID,
        account_id: currentPSAP.account_id,
        discrepancy_type: formData.discrepancy_type,
        call_type: formData.call_type || undefined,
        original_telephone_number: formData.original_telephone_number,
        original_name: formData.original_name || undefined,
        original_address: formData.original_address,
        original_community: formData.original_community || undefined,
        original_esn: formData.original_esn || undefined,
        corrected_telephone_number:
          formData.corrected_telephone_number || undefined,
        corrected_name: formData.corrected_name || undefined,
        corrected_address: formData.corrected_address || undefined,
        corrected_community: formData.corrected_community || undefined,
        corrected_esn: formData.corrected_esn || undefined,
        comments: formData.comments || undefined,
        call_timestamp: callTimestamp?.toISOString() || undefined,
      };

      // Submit to backend
      const apiHost = getAPIHost();
      const url = `${apiHost}/v1/ali/discrepancies`;

      const fetchRetry = createFetchRetry(store);
      const response = await fetchRetry(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-RapidSOS-Org': orgName,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.error ||
            `Failed to submit discrepancy: ${response.status}`,
        );
      }

      const result: DiscrepancySubmitResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to submit discrepancy report');
      }

      setSuccess(true);
      return true;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      return false;
    }
  };

  return {
    submitDiscrepancy,
    error,
    success,
  };
};
